import React, { useState } from "react";
import { Box, Typography, Paper, IconButton, CircularProgress } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Cell,
  Tooltip,
} from "recharts";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { transformMonthlySummary } from ".";

interface WSMonthlyStockChartProps {
  monthlySummary: Record<string, number> | null;
  loading?: boolean;
}

const WSMonthlyStockChart: React.FC<WSMonthlyStockChartProps> = ({
  monthlySummary,
  loading
}) => {
  const [page, setPage] = useState(0);
  const fullData = transformMonthlySummary(monthlySummary);

  const ITEMS_PER_PAGE = 6;
  const totalPages = Math.ceil(fullData.length / ITEMS_PER_PAGE);

  const handleNext = () => {
    if (page < totalPages - 1) setPage(page + 1);
  };

  const handlePrev = () => {
    if (page > 0) setPage(page - 1);
  };

  const visibleData = fullData.slice(
    page * ITEMS_PER_PAGE,
    (page + 1) * ITEMS_PER_PAGE
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: "20px",
        background: "#f9fafb",
        border: "1px solid #e5e7eb",
        maxWidth: 600,
      }}
    >
      { }
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography fontSize={22} fontWeight={600}>
          Monthly Stock Summary
        </Typography>

        <Box>
          <IconButton
            size="small"
            onClick={handlePrev}
            disabled={page === 0}
            sx={{ bgcolor: "#e0e0e0", mr: 1 }}
          >
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>

          <IconButton
            size="small"
            onClick={handleNext}
            disabled={page === totalPages - 1}
            sx={{ bgcolor: "#000", color: "#fff" }}
          >
            <ArrowForwardIosIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      { }
      {loading ? <Box
        sx={{
          width: "100%",
          height: 320,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress color="success" />
      </Box> : <Box sx={{ width: "100%", height: 385, mt: 3 }}>
        <ResponsiveContainer>
          <BarChart data={visibleData}>
            <XAxis
              dataKey="month"
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#666" }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#666" }}
              domain={[0, Math.max(...fullData.map((d) => d.value), 80)]}
              tickFormatter={(value) => `${value}`}
            />

            <Tooltip
              contentStyle={{
                borderRadius: "10px",
                border: "1px solid #e5e7eb",
              }}
              formatter={(value: number) => [`${value}`, "Stock"]}
              labelFormatter={(label) => `Month: ${label}`}
            />

            <Bar dataKey="value" radius={[8, 8, 0, 0]} barSize={40}>
              {visibleData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
      }
    </Paper>
  );
};

export default WSMonthlyStockChart;